import React, { useRef, useEffect, useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { MdFormatListBulleted as MdFormatListBulletedIcon } from 'react-icons/md'
import { useHistory } from 'react-router-dom'

import { Page, Container, ContainerItem, Table, Fab, Box } from 'components'
import CustomDatePicker from 'components/Table/components/CustomDatePicker'

import { Typography, useTheme } from '@material-ui/core'
import { isDate, format } from 'date-fns'
import _ from 'lodash'
import { DateTime } from 'luxon'
import { Column, Filter, Query } from 'material-table'

import featureFlags from 'commons/constants/featureFlags'
import permissions from 'commons/constants/permissions'
import useCheckoutStatus from 'hooks/checkoutStatus'
import useComplexity from 'hooks/complexity'
import useCriticality from 'hooks/criticality'
import useDeadlineStatus from 'hooks/deadlineStatus'
import { useFeatureFlags } from 'hooks/featureFlags'
import { useRoles } from 'hooks/roles'
import useStatus from 'hooks/status'
import { getAll } from 'services/jarvisApi/complaint'

import { Form, Input } from '../../../components/HookForm'

interface IComplaintData {
  owner?: string
  status: string
  created_at: string
  closed_at?: string
  checkout_status: string
  complexity?: string
  criticality?: string
  deadline?: string
  deadlineStatus?: string
}

const Complaint: React.FC = () => {
  const [hasFilterOnSession, setHasFilterOnSession] = useState(false)
  const [searchText, setSearchText] = useState(sessionStorage.getItem('complaint:list:search') || '')
  const clientTableRef = useRef<any>({ onQueryChange: () => {} })
  const sessionStorageCacheKey = 'complaint:list:filters'
  const searchStorageCacheKey = 'complaint:list:search'
  const { hasFeatureFlag } = useFeatureFlags()
  const { hasRoles } = useRoles()
  const hasSlaAnalysis = hasFeatureFlag(featureFlags.SLA_ANALYSIS)
  const canOpenComplaint = hasRoles([permissions.COMPLAINTS_ACCESS])

  const methods = useForm()

  // This effect was causing an infinite update loop when combined with other state updates
  // We'll use the second useEffect below to handle all initialization and updates from sessionStorage

  useEffect(() => {
    // Initialize component state from sessionStorage - only run once on mount
    const hasFilters = Boolean(sessionStorage.getItem(sessionStorageCacheKey))
    const hasSearch = Boolean(sessionStorage.getItem(searchStorageCacheKey))
    const searchValue = sessionStorage.getItem(searchStorageCacheKey) || ''
    const savedFilters = JSON.parse(sessionStorage.getItem(sessionStorageCacheKey) || '[]')

    // Update state based on sessionStorage values
    setHasFilterOnSession(hasFilters || hasSearch)
    setSearchText(searchValue)

    methods.reset({
      search: searchValue
    })

    // If there are filters in sessionStorage, apply them to the table
    if (clientTableRef.current && clientTableRef.current.dataManager) {
      // First, make sure the table is initialized
      setTimeout(() => {
        if (savedFilters.length > 0) {
          // Apply each saved filter to the table
          savedFilters.forEach((filter: Record<string, any>) => {
            const fieldName = Object.keys(filter)[0]
            const value = filter[fieldName]

            // Find the column index and column for this field
            const columnIndex = clientTableRef.current.dataManager.columns.findIndex(
              (col: any) => col.field === fieldName
            )

            if (columnIndex !== -1) {
              const column = clientTableRef.current.dataManager.columns[columnIndex]

              // Convert value based on column type
              let processedValue = value

              // Handle date columns
              if (column.type === 'date' && typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                try {
                  // Convert ISO date string to Date object for the date picker
                  processedValue = new Date(value)
                } catch (e) {
                  console.error('Failed to parse date:', value)
                }
              }

              // Apply the filter to the table
              clientTableRef.current.dataManager.changeFilterValue(columnIndex, processedValue)

              // Also trigger the onFilterChange handler to update the UI
              if (clientTableRef.current.onFilterChange) {
                clientTableRef.current.onFilterChange(columnIndex, processedValue)
              }
            }
          })
        }

        // Refresh the data with the applied filters
        if ((hasFilters || hasSearch) && clientTableRef.current) {
          clientTableRef.current.retry()
        }
      }, 100) // Small delay to ensure table is fully initialized
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const router = useHistory()

  const theme = useTheme()

  const statusHook = useStatus()
  const checkoutStatusHook = useCheckoutStatus()
  const complexityHook = useComplexity()
  const criticalityHook = useCriticality()
  const deadlineStatusHook = useDeadlineStatus()

  CustomDatePicker.displayName = 'Teste'

  const isOwnerChangeColor = (owner?: string): 'primary' | 'error' => {
    if (owner === 'Comitê de Compliance') {
      return 'primary'
    }
    return 'error'
  }

  const baseColumns: Column<IComplaintData>[] = [
    { title: 'ID', field: 'id', type: 'numeric', align: 'left', filterCellStyle: { float: 'left' } },
    { title: 'Tema', field: 'topic.group', sorting: false },
    { title: 'Chave de Acesso', field: 'access_key' },
    // eslint-disable-next-line react/display-name
    { title: 'Responsável', field: 'owner', render: (row) => (<Typography style={{ fontSize: 'inherit' }} color={isOwnerChangeColor(row.owner)}>{row.owner}</Typography>) },
    {
      title: 'Data de Criação',
      render: (row) => DateTime.fromISO(row.created_at).toFormat('dd/MM/yyyy'),
      field: 'created_at',
      type: 'date',
      defaultSort: 'desc',
      dateSetting: { locale: 'pt-BR', format: 'dd/MM/yyyy' }
    },
    {
      title: 'Data da Conclusão',
      render: (row) => row.closed_at ? DateTime.fromISO(row.closed_at).toFormat('dd/MM/yyyy') : null,
      field: 'closed_at',
      type: 'date',
      defaultSort: 'desc',
      dateSetting: { locale: 'pt-BR', format: 'dd/MM/yyyy' }
    },
    {
      title: 'Procedência',
      field: 'checkout_status',
      lookup: _.mapValues(checkoutStatusHook, (value) => value.text),
      render: (row) => ((row.checkout_status && checkoutStatusHook[row.checkout_status].text) || checkoutStatusHook.NOT_CLASSIFIED.text)
    },
    { title: 'Status', field: 'status', lookup: _.mapValues(statusHook, (value) => value.text) }
  ]

  const slaColumns: Column<IComplaintData>[] = [
    {
      title: 'Deadline',
      render: (row) => row.deadline ? DateTime.fromISO(row.deadline).toFormat('dd/MM/yyyy') : '',
      field: 'deadline',
      type: 'date',
      dateSetting: { locale: 'pt-BR', format: 'dd/MM/yyyy' }
    },
    {
      title: 'Status do Prazo',
      field: 'deadlineStatus',
      lookup: _.mapValues(deadlineStatusHook, (value) => value.text),
      // eslint-disable-next-line react/display-name
      render: (row) => {
        if (!row.deadlineStatus) return ''
        const status = deadlineStatusHook[row.deadlineStatus]
        return status
          ? (
            <Typography style={{ fontSize: 'inherit', color: status.color }}>
              {status.text}
            </Typography>
          )
          : ''
      }
    },
    {
      title: 'Complexidade',
      field: 'complexity',
      lookup: _.mapValues(complexityHook, (value) => value.text),
      render: (row) => ((row.complexity && complexityHook[row.complexity]?.text) || '')
    },
    {
      title: 'Criticidade',
      field: 'criticality',
      lookup: _.mapValues(criticalityHook, (value) => value.text),
      render: (row) => ((row.criticality && criticalityHook[row.criticality]?.text) || '')
    }
  ]

  // Conditionally include SLA columns based on feature flag
  const columns: Column<IComplaintData>[] = hasSlaAnalysis
    ? [...baseColumns.slice(0, 6), ...slaColumns, ...baseColumns.slice(6)]
    : baseColumns

  const editBtn = <MdFormatListBulletedIcon size={20} color={theme.palette.primary.main}/>

  const fetchRemoteData = async (query: Query<IComplaintData>) => {
    const filtersBeforeFetchData = JSON.parse(sessionStorage.getItem(sessionStorageCacheKey) || '[]')
    const params = {
      page: query.page + 1,
      pageSize: query.pageSize,
      filters: [],
      search: searchText || undefined
    } as any

    if (query.orderBy) {
      Object.assign(params, {
        orderBy: query.orderBy.field,
        orderDirection: query.orderDirection
      })
    }

    // Process filters from the query
    if (query.filters.length > 0) {
      const filters = query.filters
        .filter(filter => Boolean(filter.column.field))
        .filter(filter => {
          // Properly check if value exists
          if (filter.value === undefined || filter.value === null || filter.value === '') {
            return false
          }
          if (Array.isArray(filter.value)) {
            return filter.value.length > 0
          }
          return true
        })
        .map(filter => {
          if (isDate(filter.value)) {
            // Format date for API
            const date = format(filter.value, 'yyyy-MM-dd')
            return { [`${filter.column.field}`]: date }
          } else if (filter.value && typeof filter.value === 'string' && filter.value.match(/^\d{4}-\d{2}-\d{2}/)) {
            // Handle ISO date strings that might come from sessionStorage
            return { [`${filter.column.field}`]: filter.value }
          } else if (filter.column.type === 'date' && filter.value) {
            // Try to parse and format any other date-like values
            try {
              const date = new Date(filter.value)
              if (!isNaN(date.getTime())) {
                return { [`${filter.column.field}`]: format(date, 'yyyy-MM-dd') }
              }
            } catch (e) {
              console.error('Failed to parse date:', filter.value)
            }
          }
          return { [`${filter.column.field}`]: filter.value }
        })

      // Save filters to sessionStorage
      sessionStorage.setItem(sessionStorageCacheKey, JSON.stringify(filters))

      // Update UI state
      setHasFilterOnSession(true)

      // Add filters to params
      filters.forEach(filter => params.filters.push(filter))
    } else {
      // If there are no filters in the query but there were filters before, update state
      if (filtersBeforeFetchData.length > 0) {
        sessionStorage.removeItem(sessionStorageCacheKey)
        setHasFilterOnSession(Boolean(searchText))
      }
    }

    try {
      const { data } = await getAll(params)
      return ({
        data: data.data,
        page: data.meta.current_page - 1,
        totalCount: data.meta.total
      })
    } catch (err) {
      throw new Error(err)
    }
  }

  const handleClearFilters = () => {
    // Clear sessionStorage
    sessionStorage.removeItem(sessionStorageCacheKey)
    sessionStorage.removeItem(searchStorageCacheKey)

    // Update UI state
    setSearchText('')
    setHasFilterOnSession(false)

    // Update form value
    methods.reset({
      search: ''
    })

    // Reset table filters and refresh data
    if (clientTableRef.current) {
      // Clear all filters in the table
      if (clientTableRef.current.dataManager) {
        // Reset all column filters
        clientTableRef.current.dataManager.columns.forEach(
          (column: any, index: any) => {
            // Set to null instead of undefined for better compatibility
            clientTableRef.current.dataManager.changeFilterValue(index, null)
            if (clientTableRef.current.onFilterChange) {
              clientTableRef.current.onFilterChange(index, null)
            }
          }
        )

        // Don't manually modify filterData as it might be a function in the library
      }

      // Reset the query and refresh data
      clientTableRef.current.onQueryChange({
        filters: [],
        search: '',
        page: 0,
        pageSize: 10
      })

      // Force a retry to refresh the data
      setTimeout(() => {
        if (clientTableRef.current) {
          clientTableRef.current.retry()
        }
      }, 0)
    }
  }

  const onSubmitSearch = useCallback((value: any) => {
    // Get current search text directly from sessionStorage to avoid dependency on state
    const previousSearchText = sessionStorage.getItem(searchStorageCacheKey) || ''
    const newSearchText = value.search || ''

    // Only proceed if the search text has changed
    if (previousSearchText !== newSearchText) {
      // Update state and sessionStorage
      setSearchText(newSearchText)

      // Update filter visibility state
      const hasSearch = Boolean(newSearchText)
      const hasFilters = Boolean(sessionStorage.getItem(sessionStorageCacheKey))
      setHasFilterOnSession(hasSearch || hasFilters)

      // Update sessionStorage
      if (newSearchText) {
        sessionStorage.setItem(searchStorageCacheKey, newSearchText)
      } else {
        sessionStorage.removeItem(searchStorageCacheKey)
      }
    }

    // Always refresh table data, even if search text hasn't changed
    // This ensures the UI is updated properly
    if (clientTableRef.current) {
      clientTableRef.current.retry()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Page title="Denúncias">
      <Container spacing={3} justifyContent={'center'}>
        <ContainerItem paper xs={12} sm={4}>
          <Form onSubmit={onSubmitSearch} methods={methods}>
            <Box display="flex" alignItems="center" width={1}>
              <Box width={hasFilterOnSession ? '60%' : '80%'}>
                <Input
                  label="Buscar"
                  name="search"
                  variant={'outlined'}
                  fullWidth
                />
              </Box>
              <Fab
                variant="extended"
                color="primary"
                type='submit'
                style={{ marginLeft: '10px' }}
              >
                  Buscar
              </Fab>
              {hasFilterOnSession && (<Box ml={2} width='20%'>
                <Fab
                  variant="extended"
                  color="primary"
                  onClick={handleClearFilters}
                >
                  Limpar Filtros
                </Fab>
              </Box>)}
            </Box>
          </Form>
        </ContainerItem>
        <ContainerItem paper={false} xs={12}>
          <Table
            tableRef={clientTableRef}
            data={(query) => fetchRemoteData(query)}
            columns={columns}
            onFilterChange={(filters: Filter<IComplaintData>[]) => {
              // Update UI state to show the "Clear Filters" button
              const hasActiveFilters = filters.some(filter =>
                filter.value !== undefined && filter.value !== null && filter.value !== ''
              )
              const hasSearch = Boolean(searchText)
              setHasFilterOnSession(hasActiveFilters || hasSearch)
            }}
            actions={
              canOpenComplaint
                ? [
                  {
                    icon: () => editBtn,
                    tooltip: 'Abrir',
                    onClick: (_e, rowData) => router.push(`/complaint/${rowData.id}`)
                  }
                ]
                : []}
            options={{ actionsColumnIndex: -1, pageSize: 10, filtering: true }}
          />
        </ContainerItem>
      </Container>
    </Page>
  )
}

export default Complaint
